import { useState } from "react";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import ModalFrame from "../../../components/ModalFrame";
import { Loading } from "../../../components/Loading";
import { LoadingTextSkeleton } from "../../../components/LoadingTextSkeleton";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import { useViewDraftHistoriesQuery, DraftHistory } from "./draftAutosaveHistoryApiSlice";
import AutosaveHistoryForDraftContent from "./AutosaveHistoryForDraftContent";

interface AutosaveHistoryForDraftProps {
	draftId: number | undefined;
	promptTitle: string | undefined;
	draftDateTime: string | undefined;
	dialogOpen: boolean;
	resetDialog: () => void;
}

export default function DraftAutosaveHistoryModal({
	draftId,
	promptTitle,
	draftDateTime,
	dialogOpen,
	resetDialog,
}: AutosaveHistoryForDraftProps) {
	// Initialization
	if (!draftId) return null;
	const draftHistoriesResult = useViewDraftHistoriesQuery({
		draftId: draftId,
	});

	// Table
	const draftHistories = draftHistoriesResult.data?.draftHistories;

	const handleRowClick = (event: DataTableRowClickEvent) => {
		setSelectedHistoryId(event.data.id);
		setSelectedRow(event.data as DraftHistory);
	};

	// Selecting autosave content
	const [selectedHistoryId, setSelectedHistoryId] = useState<number>();
	const [selectedRow, setSelectedRow] = useState<DraftHistory | null>(null);

	// Dialog
	const handleDialogClose = () => {
		setSelectedHistoryId(undefined);
		setSelectedRow(null);
		resetDialog();
	};

	return (
		<ModalFrame open={dialogOpen} onClose={handleDialogClose}>
			<div className="flex justify-between flex-wrap gap-2">
				{draftHistoriesResult.isLoading ? (
					<>
						<LoadingTextSkeleton className="mb-1" />
						<LoadingTextSkeleton className="mb-1" />
					</>
				) : (
					<>
						{promptTitle && (
							<p className="font-medium min-w-0 whitespace-normal break-words">
								{promptTitle}
							</p>
						)}
						{draftDateTime && <p className="font-medium">Started {draftDateTime}</p>}
					</>
				)}
			</div>
			<Loading loading={draftHistoriesResult.isLoading} className="mt-4! h-[200px]" />
			{draftHistoriesResult.data && (
				<>
					<DataTable
						value={draftHistories}
						dataKey="id"
						stripedRows
						emptyMessage="No results found"
						className="tbl tbl-data tbl-datatable mt-4"
						id="draft-autosave-history-modal-table"
						scrollable
						scrollHeight="200px"
						selectionMode="single"
						selection={selectedRow}
						onRowClick={handleRowClick}
						rowClassName={() => "group cursor-pointer"}>
						<Column
							field="savedOn"
							header="Saved On"
							body={(row: DraftHistory) =>
								row.savedOn ? <FormattedDateAndTime date={row.savedOn} /> : "N/A"
							}
						/>
						<Column field="length" header="Length" />
						<Column field="status" header="Status" />
					</DataTable>
					<AutosaveHistoryForDraftContent
						key={selectedHistoryId}
						historyId={selectedHistoryId}
					/>
					<div className="mt-4 grid grid-cols-2 gap-3">
						<div></div>
						<button
							type="button"
							id="close-button"
							onClick={handleDialogClose}
							className="btn btn-neutral order-2">
							Close
						</button>
					</div>
				</>
			)}
		</ModalFrame>
	);
}
